import {
    Box, Typography, Button, Modal, TextField, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, Skeleton, Avatar, Select,
    MenuItem, FormControl, InputLabel, Checkbox, LinearProgress, Alert, Tooltip
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Toast } from 'primereact/toast';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CustomAxios from "../../utils/CustomAxios";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

const STATUS_STYLE = {
    initiated: { bg: '#FFF7E6', border: '#FA8C16', color: '#FA8C16' },
    in_progress: { bg: '#E6F4FF', border: '#0071BC', color: '#0071BC' },
    completed: { bg: '#F6FFED', border: '#52C41A', color: '#52C41A' },
    cancelled: { bg: '#FAFAFA', border: '#BFBFBF', color: '#6B7280' },
};

function Chip({ style, label }) {
    return (
        <Box sx={{
            display: 'inline-flex', justifyContent: 'center',
            backgroundColor: style.bg, border: '1px solid', borderColor: style.border,
            borderRadius: '2px', padding: '1px 8px', whiteSpace: 'nowrap'
        }}>
            <Typography sx={{ fontSize: '11px', lineHeight: '20px', color: style.color, textTransform: 'uppercase' }}>
                {label}
            </Typography>
        </Box>
    );
}

function initialsOf(name) {
    if (!name) return '--';
    return name.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

export function StaffRelieving() {
    const selectedHub = useSelector(state => state.user.selected_hub);
    const toast = useRef(null);
    const userRole = localStorage.getItem('role');
    const isHR = ['super_admin', 'hr_admin'].includes(userRole);

    const [list, setList] = useState([]);
    const [selectedStaffId, setSelectedStaffId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [letterOpen, setLetterOpen] = useState(false);
    const [letterBody, setLetterBody] = useState('');
    const [letterMeta, setLetterMeta] = useState({ company_name: '', signatory: '', signatory_title: '' });

    const hubId = useMemo(() => {
        const normalize = (value) => {
            if (value === null || value === undefined) return null;
            if (Array.isArray(value)) return normalize(value[0]);
            const n = Number(value);
            return Number.isFinite(n) ? n : null;
        };
        const fromStore = normalize(selectedHub);
        if (fromStore) return fromStore;
        const stored = localStorage.getItem('selected_hub');
        if (!stored) return null;
        try {
            return normalize(JSON.parse(stored));
        } catch (e) {
            return normalize(stored);
        }
    }, [selectedHub]);

    const showToast = (severity, summary, detailText) => {
        if (toast.current) toast.current.show({ severity, summary, detail: detailText, life: 4500 });
    };

    const errorMessage = (error, fallback) => {
        const message = error?.response?.data?.error;
        if (!message) return fallback;
        return typeof message === 'string' ? message : fallback;
    };

    useEffect(() => {
        if (!hubId) return;
        fetchList();
    }, [hubId]);

    useEffect(() => {
        if (!selectedStaffId) return;
        fetchDetail(selectedStaffId);
    }, [selectedStaffId]);

    const fetchList = async () => {
        try {
            setLoading(true);
            const res = await CustomAxios.get('staff/relieving/', { params: { hub_id: hubId } });
            setList(res.data || []);
            if (res.data?.length && !selectedStaffId) setSelectedStaffId(res.data[0].staff);
            if (!res.data?.length) setDetail(null);
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Failed to load relievings'));
        } finally {
            setLoading(false);
        }
    };

    const fetchDetail = async (staffId) => {
        try {
            setDetailLoading(true);
            const res = await CustomAxios.get(`staff/relieving/${staffId}/`);
            setDetail(res.data);
            setLetterBody(res.data.letter_draft || '');
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Failed to load the record'));
            setDetail(null);
        } finally {
            setDetailLoading(false);
        }
    };

    const toggleItem = async (item, done) => {
        if (!detail) return;
        try {
            setSaving(true);
            const res = await CustomAxios.put(`staff/relieving/${selectedStaffId}/checklist/`, {
                items: [{ item: item.item, done, remarks: item.remarks || '' }],
            });
            setDetail(prev => ({ ...prev, record: res.data }));
            fetchList();
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Could not update the checklist'));
        } finally {
            setSaving(false);
        }
    };

    const saveRemark = async (item, remarks) => {
        try {
            await CustomAxios.put(`staff/relieving/${selectedStaffId}/checklist/`, {
                items: [{ item: item.item, done: item.done, remarks }],
            });
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Could not save the remark'));
        }
    };

    const cancelResignation = async () => {
        if (!window.confirm(
            `Cancel the resignation for ${record?.staff_name}? They return to Active and the checklist is kept.`
        )) return;
        try {
            setSaving(true);
            await CustomAxios.put(`staff/relieving/${selectedStaffId}/cancel/`);
            showToast('success', 'Cancelled', 'Staff member returned to active');
            setSelectedStaffId(null);
            setDetail(null);
            fetchList();
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Could not cancel'));
        } finally {
            setSaving(false);
        }
    };

    const generateLetter = async () => {
        if (!letterBody.trim()) {
            showToast('error', 'Nothing to issue', 'The letter body cannot be empty.');
            return;
        }
        try {
            setSaving(true);
            await CustomAxios.post(`staff/relieving/${selectedStaffId}/letter/`, {
                body_text: letterBody,
                ...letterMeta,
            });
            showToast('success', 'Issued', 'Relieving letter generated and stored');
            setLetterOpen(false);
            fetchDetail(selectedStaffId);
            fetchList();
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Could not generate the letter'));
        } finally {
            setSaving(false);
        }
    };

    const record = detail?.record;
    const openItems = detail?.open_items;
    const progress = record?.checklist_progress;
    const progressPct = progress?.total ? (progress.done / progress.total) * 100 : 0;

    return (
        <Box sx={{ p: 3 }}>
            <Toast ref={toast} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <Typography variant="h5">
                    Relieving{record ? ` · ${record.staff_name}` : ''}
                </Typography>
                <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: '250px' }}>
                        <InputLabel>Staff on notice</InputLabel>
                        <Select
                            value={selectedStaffId || ''} label="Staff on notice"
                            onChange={(e) => setSelectedStaffId(e.target.value)}
                        >
                            {list.map(r => (
                                <MenuItem key={r.id} value={r.staff}>
                                    {r.staff_name} · {r.employee_id}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {record && record.status !== 'completed' && (
                        <Button variant="outlined" onClick={cancelResignation} disabled={saving}>
                            Cancel resignation
                        </Button>
                    )}
                    {record && (
                        <Tooltip
                            arrow
                            title={record.can_generate_letter
                                ? 'Review the wording before issuing'
                                : `Blocked by: ${record.mandatory_pending.join(', ')}`}
                        >
                            <span>
                                <Button
                                    variant="contained"
                                    startIcon={<DescriptionOutlinedIcon />}
                                    disabled={!record.can_generate_letter || !isHR || saving}
                                    onClick={() => setLetterOpen(true)}
                                >
                                    {record.letter_generated_on ? 'Reissue letter' : 'Generate relieving letter'}
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            {!loading && !list.length && (
                <Alert severity="info" sx={{ fontSize: '13px' }}>
                    Nobody is on notice at this hub. Start a relieving from the Staff Directory —
                    the <b>···</b> menu on a staff row has <b>Mark for relieving</b>.
                </Alert>
            )}

            {detailLoading ? (
                [0, 1, 2].map(i => <Skeleton key={i} animation="wave" sx={{ height: '60px' }} />)
            ) : record ? (
                <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: '1 1 520px', minWidth: '320px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <Box sx={{ backgroundColor: 'background.light', borderRadius: '12px', boxShadow: '0px 0px 25px 0px #00000012', overflow: 'hidden' }}>
                            <Box sx={{ padding: '13px 18px', borderBottom: '1px solid #EDEDED', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                                <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>Clearance checklist</Typography>
                                <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                                    {progress?.done} of {progress?.total} done
                                    {record.mandatory_pending.length ? ` · ${record.mandatory_pending.length} mandatory pending` : ''}
                                </Typography>
                            </Box>
                            <Box sx={{ px: '18px', pt: '10px' }}>
                                <LinearProgress
                                    variant="determinate" value={progressPct}
                                    sx={{
                                        height: 5, borderRadius: '3px',
                                        '& .MuiLinearProgress-bar': { backgroundColor: '#52C41A' },
                                    }}
                                />
                            </Box>
                            <Box>
                                {(record.checklist || []).map((item, i) => (
                                    <Box key={i} sx={{
                                        display: 'flex', alignItems: 'flex-start', gap: '10px',
                                        padding: '12px 18px', borderBottom: '1px solid #EDEDED'
                                    }}>
                                        <Checkbox
                                            size="small" checked={Boolean(item.done)}
                                            disabled={record.status === 'completed' || saving}
                                            onChange={(e) => toggleItem(item, e.target.checked)}
                                            sx={{ padding: '2px', color: '#52C41A', '&.Mui-checked': { color: '#52C41A' } }}
                                        />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                                                {item.item}
                                                {item.mandatory && (
                                                    <Typography component="span" sx={{ ml: 1, fontSize: '9px', color: '#F5222D', textTransform: 'uppercase' }}>
                                                        mandatory
                                                    </Typography>
                                                )}
                                            </Typography>
                                            <TextField
                                                variant="standard" placeholder="Add a remark"
                                                defaultValue={item.remarks || ''}
                                                disabled={record.status === 'completed'}
                                                onBlur={(e) => {
                                                    if (e.target.value !== (item.remarks || '')) {
                                                        saveRemark(item, e.target.value);
                                                    }
                                                }}
                                                sx={{ mt: 0.5, width: '100%', '& input': { fontSize: '11px' } }}
                                            />
                                            {item.completed_on && (
                                                <Typography sx={{ fontSize: '10px', color: 'text.secondary', mt: 0.5 }}>
                                                    Done {dayjs(item.completed_on).format('DD MMM YYYY')}
                                                    {item.completed_by ? ` by ${item.completed_by}` : ''}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        <TableContainer component={Paper} sx={{ borderRadius: '12px', boxShadow: '0px 0px 25px 0px #00000012' }}>
                            <Box sx={{ padding: '13px 18px', borderBottom: '1px solid #EDEDED', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                                <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>Open items from other screens</Typography>
                                <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>read-only</Typography>
                            </Box>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        {['Source', 'Item', 'Status'].map((h, i) => (
                                            <TableCell key={i} sx={{ fontWeight: 600, backgroundColor: 'background.table', fontSize: '11px' }}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {openItems?.compliance?.map(c => (
                                        <TableRow key={`c-${c.id}`}>
                                            <TableCell sx={{ fontSize: '12px' }}>Compliance</TableCell>
                                            <TableCell sx={{ fontSize: '12px' }}>
                                                {dayjs(c.incident_date).format('DD MMM')} · {c.description} ({c.severity_label})
                                            </TableCell>
                                            <TableCell><Chip style={STATUS_STYLE.initiated} label={c.status_label} /></TableCell>
                                        </TableRow>
                                    ))}
                                    {openItems?.unused_comp_offs?.map(c => (
                                        <TableRow key={`o-${c.id}`}>
                                            <TableCell sx={{ fontSize: '12px' }}>Comp-off</TableCell>
                                            <TableCell sx={{ fontSize: '12px' }}>
                                                Earned {dayjs(c.earned_on).format('DD MMM')}, expires {dayjs(c.expires_on).format('DD MMM')}
                                            </TableCell>
                                            <TableCell><Chip style={STATUS_STYLE.initiated} label="Unused" /></TableCell>
                                        </TableRow>
                                    ))}
                                    {openItems?.unresolved_punches?.map(a => (
                                        <TableRow key={`a-${a.id}`}>
                                            <TableCell sx={{ fontSize: '12px' }}>Attendance</TableCell>
                                            <TableCell sx={{ fontSize: '12px' }}>
                                                Missing punch on {dayjs(a.date).format('DD MMM YYYY')}
                                            </TableCell>
                                            <TableCell><Chip style={{ bg: '#FFF1F0', border: '#F5222D', color: '#F5222D' }} label="Unresolved" /></TableCell>
                                        </TableRow>
                                    ))}
                                    {!openItems?.counts?.compliance && !openItems?.counts?.unused_comp_offs
                                        && !openItems?.counts?.unresolved_punches && (
                                        <TableRow><TableCell colSpan={3} sx={{ textAlign: 'center', py: 3, fontSize: '12px' }}>
                                            Nothing outstanding elsewhere
                                        </TableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    <Box sx={{ flex: '1 1 300px', minWidth: '280px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <Box sx={{ backgroundColor: 'background.light', borderRadius: '12px', boxShadow: '0px 0px 25px 0px #00000012', overflow: 'hidden' }}>
                            <Box sx={{ padding: '13px 18px', borderBottom: '1px solid #EDEDED', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar sx={{ width: 30, height: 30, fontSize: '11px', bgcolor: 'background.table', color: 'button.blue' }}>
                                    {initialsOf(record.staff_name)}
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>{record.staff_name}</Typography>
                                    <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>{record.employee_id}</Typography>
                                </Box>
                                <Chip style={STATUS_STYLE[record.status]} label={record.status_label} />
                            </Box>
                            <Box sx={{ p: '15px 18px', display: 'flex', flexDirection: 'column', gap: '9px' }}>
                                {[
                                    ['Designation', [record.designation, record.department_name].filter(Boolean).join(' · ') || '—'],
                                    ['Hub', record.hub_name || '—'],
                                    ['Date of joining', record.date_of_joining ? dayjs(record.date_of_joining).format('DD MMM YYYY') : '—'],
                                    ['Resignation received', dayjs(record.resignation_date).format('DD MMM YYYY')],
                                    ['Notice period', `${record.notice_period_days} days`],
                                    ['Last working day', dayjs(record.last_working_day).format('DD MMM YYYY')],
                                    ['Total service', record.tenure_label || '—'],
                                    ['Reason', record.reason || '—'],
                                    ['Login account', record.has_login ? 'Linked — disabled on LWD' : 'None'],
                                    ['Processed by', record.processed_by_name || '—'],
                                ].map(([label, value]) => (
                                    <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                        <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>{label}</Typography>
                                        <Typography sx={{ fontSize: '12px', fontWeight: 500, textAlign: 'right' }}>{value}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        <Box sx={{ backgroundColor: 'background.light', borderRadius: '12px', boxShadow: '0px 0px 25px 0px #00000012', overflow: 'hidden' }}>
                            <Box sx={{ padding: '13px 18px', borderBottom: '1px solid #EDEDED' }}>
                                <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>Letter</Typography>
                            </Box>
                            <Box sx={{ p: '15px 18px', display: 'flex', flexDirection: 'column', gap: '9px' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>Status</Typography>
                                    <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
                                        {record.letter_generated_on
                                            ? `Issued ${dayjs(record.letter_generated_on).format('DD MMM YYYY')}`
                                            : 'Not generated'}
                                    </Typography>
                                </Box>
                                {!record.can_generate_letter && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                        <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>Blocked by</Typography>
                                        <Typography sx={{ fontSize: '12px', textAlign: 'right', color: 'borders.red' }}>
                                            {record.mandatory_pending.join(', ')}
                                        </Typography>
                                    </Box>
                                )}
                                {record.letter_url && (
                                    <Button
                                        variant="outlined" size="small" sx={{ mt: 1 }}
                                        onClick={() => window.open(record.letter_url, '_blank', 'noopener')}
                                    >
                                        Open issued letter
                                    </Button>
                                )}
                                {!isHR && (
                                    <Typography sx={{ fontSize: '11px', color: 'text.secondary', mt: 1 }}>
                                        Issuing the letter is restricted to HR admins.
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            ) : null}

            <Modal open={letterOpen} onClose={() => setLetterOpen(false)}>
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)', width: { xs: '94%', md: 660 },
                    bgcolor: 'background.paper', boxShadow: 24, p: 3,
                    borderRadius: 2, maxHeight: '90vh', overflow: 'auto'
                }}>
                    <Typography variant="h6" sx={{ mb: 0.5 }}>Review the relieving letter</Typography>
                    <Typography sx={{ fontSize: '12px', color: 'text.secondary', mb: 2 }}>
                        {record?.staff_name} · {record?.employee_id}
                    </Typography>

                    <Alert severity="warning" sx={{ mb: 2, fontSize: '12.5px' }}>
                        The wording below is a <b>draft template, not approved legal text</b>. Read it and
                        edit it before issuing — this letter goes to a former employee and may be relied on.
                    </Alert>

                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                        <TextField
                            label="Company name on letterhead" size="small" sx={{ flex: 1, minWidth: '180px' }}
                            placeholder={record?.hub_name ? 'Defaults to the franchise name' : ''}
                            value={letterMeta.company_name}
                            onChange={(e) => setLetterMeta({ ...letterMeta, company_name: e.target.value })}
                        />
                        <TextField
                            label="Signatory" size="small" sx={{ flex: 1, minWidth: '150px' }}
                            value={letterMeta.signatory}
                            onChange={(e) => setLetterMeta({ ...letterMeta, signatory: e.target.value })}
                        />
                        <TextField
                            label="Signatory title" size="small" sx={{ flex: 1, minWidth: '150px' }}
                            placeholder="Authorised Signatory"
                            value={letterMeta.signatory_title}
                            onChange={(e) => setLetterMeta({ ...letterMeta, signatory_title: e.target.value })}
                        />
                    </Box>

                    <TextField
                        fullWidth multiline minRows={12} label="Letter body"
                        value={letterBody}
                        onChange={(e) => setLetterBody(e.target.value)}
                        sx={{ mb: 2, '& textarea': { fontSize: '13px', lineHeight: 1.6 } }}
                    />

                    <Typography sx={{ fontSize: '11px', color: 'text.secondary', mb: 2 }}>
                        Blank lines separate paragraphs. Issuing marks the relieving complete and stores
                        the PDF privately.
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button onClick={() => setLetterOpen(false)}>Cancel</Button>
                        <Button variant="contained" onClick={generateLetter} disabled={saving}>
                            {saving ? 'Generating…' : 'Issue letter'}
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
}
